import { num, round3 } from '../leaseGeneralInvoiceReceipt/allocationHelpers';
import { emptyToNull, toApiDate } from '../leaseGeneralInvoiceReceipt/formUtils';

export const MAX = { chequeNo: 50, drawnOnBank: 100, refNo: 50, narration: 1000 };
export const DEFAULT_TRANSACTION_TYPE = 'PDC';
export const MIN_CHEQUE_DATE = new Date(2000, 0, 1);

export const MESSAGES = {
    amountBlank: 'Amount should not be blank or zero.',
    debitAccount: 'Select Debit Account.',
    narration: 'Narration Required',
    chequeNo: 'Cheque No required',
    drawnOnBank: 'Invalid Drawn On Bank.',
    chequeDate: 'Invalid Cheque Date.',
    property: 'Select Property.',
    unit: 'Select Unit No.',
    lease: 'Select Lease No.',
    tenant: 'Select Tenant.',
    paymentType: 'Select Payment Type.',
    noAccount: 'Account is not defined for selected payment type.',
    allocateAmount: 'Enter Allocated Amount.',
    exceeded: 'Amount exceeded.',
    duplicateLine: 'Payment type already exists for this lease in the receipt.',
    noLines: 'Amount allocate not completed.',
    notCompleted: 'Payment not completed.',
};

/** Total amount minus the sum of the lines, rounded to 3 decimals. */
export const balance = (totalAmount, lines) =>
    round3(round3(totalAmount) - (lines || []).reduce((sum, line) => sum + round3(line?.amount), 0));

const HIDDEN = { label: '', visible: false };

/** Labels and visibility of the three fields the old form reuses per transaction type. */
export const fieldLabels = (transactionType) => {
    switch (transactionType) {
        case 'Cheque':
        case 'PDC':
            return {
                chequeNo: { label: 'Cheque No', visible: true },
                drawnOnBank: { label: 'Drawn On Bank', visible: true },
                chequeDate: { label: 'Cheque Date', visible: true },
            };
        case 'Credit Card':
            return {
                chequeNo: { label: 'Card Type', visible: true },
                drawnOnBank: { label: 'Card No', visible: true },
                chequeDate: { label: 'Expiry Date', visible: true },
            };
        case 'Bank Transfer':
            return {
                chequeNo: { label: 'Ref No', visible: true },
                drawnOnBank: HIDDEN,
                chequeDate: { label: 'Transfer Date', visible: true },
            };
        default:
            return { chequeNo: HIDDEN, drawnOnBank: HIDDEN, chequeDate: HIDDEN };
    }
};

export const isChequeOrPdc = (transactionType) => transactionType === 'Cheque' || transactionType === 'PDC';

/**
 * First failing rule of 4.2 (stop at the first), or null.
 * panel: { propertyNo, unitNo, leaseNo, tenantNo, paymentTypeDesc, creditAccountCode, amount }
 */
export const validateAllocation = (panel, lines, currentBalance) => {
    if (!panel?.propertyNo) return MESSAGES.property;
    if (!panel.unitNo) return MESSAGES.unit;
    if (!panel.leaseNo) return MESSAGES.lease;
    if (!panel.tenantNo) return MESSAGES.tenant;
    if (!panel.paymentTypeDesc) return MESSAGES.paymentType;
    if (`${panel.creditAccountCode ?? ''}` === '0' || !`${panel.creditAccountCode ?? ''}`.trim()) return MESSAGES.noAccount;
    const amount = round3(panel.amount);
    if (!(amount > 0)) return MESSAGES.allocateAmount;
    if (amount > round3(currentBalance)) return MESSAGES.exceeded;
    const duplicate = (lines || []).some((l) => l.leaseNo === panel.leaseNo && l.paymentTypeDesc === panel.paymentTypeDesc);
    if (duplicate) return MESSAGES.duplicateLine;
    return null;
};

/**
 * 4.1 as a field -> message map (empty when valid).
 * payment: { totalAmount, receiptDate, transactionType, debitAccountCode, narration, chequeNo, drawnOnBank, chequeDate, refNo }
 */
export const validatePayment = (payment) => {
    const errors = {};
    if (!(round3(payment.totalAmount) > 0)) errors.totalAmount = MESSAGES.amountBlank;
    if (!payment.receiptDate) errors.receiptDate = 'Receipt Date is required.';
    if (!payment.transactionType) errors.transactionType = 'Transaction Type is required.';
    if (!payment.debitAccountCode) errors.debitAccountCode = MESSAGES.debitAccount;
    const narration = `${payment.narration ?? ''}`.trim();
    if (!narration) errors.narration = MESSAGES.narration;
    else if (narration.length > MAX.narration) errors.narration = `Narration must be at most ${MAX.narration} characters.`;
    if (isChequeOrPdc(payment.transactionType)) {
        if (!`${payment.chequeNo ?? ''}`.trim()) errors.chequeNo = MESSAGES.chequeNo;
        if (!`${payment.drawnOnBank ?? ''}`.trim()) errors.drawnOnBank = MESSAGES.drawnOnBank;
        if (!payment.chequeDate || payment.chequeDate < MIN_CHEQUE_DATE) errors.chequeDate = MESSAGES.chequeDate;
    }
    if (!errors.chequeNo && `${payment.chequeNo ?? ''}`.length > MAX.chequeNo) errors.chequeNo = `Maximum ${MAX.chequeNo} characters.`;
    if (!errors.drawnOnBank && `${payment.drawnOnBank ?? ''}`.length > MAX.drawnOnBank) errors.drawnOnBank = `Maximum ${MAX.drawnOnBank} characters.`;
    if (`${payment.refNo ?? ''}`.length > MAX.refNo) errors.refNo = `Maximum ${MAX.refNo} characters.`;
    return errors;
};

/** 4.3 — error message or null. */
export const validateSave = (totalAmount, lines) => {
    if (!lines?.length) return MESSAGES.noLines;
    if (balance(totalAmount, lines) !== 0) return MESSAGES.notCompleted;
    return null;
};

/** Request body for POST /CreateReceipt: yyyy-MM-dd dates, empty strings as null, hidden fields cleared. */
export const buildCreateRequest = (payment, lines) => {
    const labels = fieldLabels(payment.transactionType);
    return {
        receiptDate: toApiDate(payment.receiptDate),
        transactionType: payment.transactionType,
        debitAccountCode: payment.debitAccountCode,
        chequeNo: labels.chequeNo.visible ? emptyToNull(payment.chequeNo) : null,
        drawnOnBank: labels.drawnOnBank.visible ? emptyToNull(payment.drawnOnBank) : null,
        chequeDate: labels.chequeDate.visible ? toApiDate(payment.chequeDate) : null,
        totalAmount: round3(payment.totalAmount),
        refNo: emptyToNull(payment.refNo),
        narration: `${payment.narration ?? ''}`.trim(),
        lines: lines.map((l) => ({
            propertyNo: l.propertyNo,
            unitNo: l.unitNo,
            leaseNo: l.leaseNo,
            tenantNo: l.tenantNo,
            paymentTypeDesc: l.paymentTypeDesc,
            creditAccountCode: l.creditAccountCode,
            amount: round3(l.amount),
        })),
    };
};

export const allocatedTotal = (lines) => round3((lines || []).reduce((sum, l) => sum + num(l.amount), 0));
